import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { IBankAccount, IBankingApi, IBankTransaction, ITransactionQuery, TransactionSort, TransactionTab } from '../../services/remits/banking-api';
import { samePayer } from '../../services/remits/remit-match';
import { IRemit, IRemitQuery, IRemitsApi, RemitKind } from '../../services/remits/remits-api';

/** The deposit list's own filters (crosswalk §4). */
export interface ITransactionFilters {
    name?: string;
    /** A deposit, as typed (positive). */
    amount?: string;
    /** `yyyy-MM-dd`. */
    date?: string;
    externalId?: string;
}

/** The remits panel's own filters (crosswalk §5). */
export interface ICandidateFilters {
    payerName?: string;
    paymentNumber?: string;
    /** A paid amount, as typed — replaces the deposit's amount when given. */
    amount?: string;
    /** `yyyy-MM-dd`. */
    paidDate?: string;
}

/** What Undo reverses: the last write, said in words, and how to reverse it. */
export interface IReconcileUndo {
    /** `Posted ERA 0084419 ($208.72) to the $208.72 deposit of Mar 4, 2025.` */
    done: string;
    label: string;
    run: () => Promise<void>;
}

/**
 * "Match on" (FE-184, Jordan 2026-10-15): which facts of the picked deposit — or, flipped, the picked remit — a match must
 * share. The checked ones AND together. Measured on production 2026-10-16 on deposits already posted to their ERA: the
 * payment number agrees exactly (142 of 142 that carry one), a deposit lands 0–8 days after the paid date, and the payer
 * agrees on 74% (see `samePayer`).
 */
export interface IMatchOn {
    /** The paid amount, within `tolerance` dollars either way — 0 is exactly. */
    amount: boolean;
    tolerance: number;
    /** The payment number the deposit's description carries (FE-183), exactly. */
    paymentNumber: boolean;
    /** Paid at most `days` before the deposit. */
    withinDays: boolean;
    days: number;
    payer: boolean;
}

/** As the design opens: the exact amount only. */
export const DEFAULT_MATCH_ON: IMatchOn = { amount: true, tolerance: 0, paymentNumber: false, withinDays: false, days: 30, payer: false };

/** A day `n` days from `yyyy-MM-dd`, as `yyyy-MM-dd`. */
export const shiftDay = (day: string, n: number): string => {
    const date = new Date(`${day.slice(0, 10)}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + n);
    return date.toISOString().slice(0, 10);
};

const TABS: TransactionTab[] = ['UNPOSTED', 'POSTED', 'IGNORED'];

const money = (value: number | string | undefined): string =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

/** The server's refusals, in words a biller can act on. */
const REFUSALS: Array<[RegExp, string]> = [
    [
        /^err\.banking_account_transaction_already_correlated/,
        'This deposit is already posted to a remit, and the server allows one remit per deposit. Unpost that one first.',
    ],
    // Backend #1744 (John, 2026-10-13): unposting an ERA the server matched by itself is a 400 that keeps the link.
    [
        /^err\.automatic_clearinghouse_era_posting_cannot_be_unlinked/,
        'This ERA was posted automatically, and the server keeps automatic postings. Only a remit posted by hand can be unposted.',
    ],
];

/** What went wrong: the generated client throws the raw `Response`, so its body carries the server's code and message. */
const describe = async (err: unknown, fallback: string): Promise<string> => {
    if (typeof Response !== 'undefined' && err instanceof Response) {
        try {
            const body = await err.clone().json();
            const known = REFUSALS.find(([code]) => code.test(String(body?.code || '')));
            return known ? known[1] : body?.message || `the server answered ${err.status}`;
        } catch {
            return `the server answered ${err.status}`;
        }
    }
    return err instanceof Error && err.message ? err.message : fallback;
};

const errorText = (err: unknown, fallback: string): string => (err instanceof Error && err.message ? err.message : fallback);

/**
 * Reconcile (FE-163): pick a bank deposit on the left, find the ERA or EOB that explains it on the right, and post it —
 * the remit's `plaidItemTransactionId` set to the deposit.
 *
 * ## Flipped (FE-185)
 *
 * The other way round: pick a remit first, and the deposit list narrows to the deposits that could carry it — its paid
 * amount, exactly or within $5 — each with its own Post Payment. Same write, same read-back, same Undo.
 *
 * ## One remit ↔ one deposit
 *
 * A remit stores ONE transaction id, so a remit is posted to one deposit, and the server refuses a second remit on a
 * deposit that has one (`…already_correlated_to_clearinghouse_era`). That is intended (backend, 2026-10-12; crosswalk §8),
 * so Post is disabled on a deposit that is taken.
 *
 * ## Writes (NS-17)
 *
 * Post, Unpost, Ignore and Restore are each one call, never retried, and each records an Undo. All four are proven on
 * production: with a Support login (2026-10-12), Ignore/Restore on an outgoing payment and Post then Unpost of one ERA on
 * the one deposit it matched exactly — linked and counted posted, then cleared and unposted, its other fields untouched;
 * with a provider login (2026-10-13), the same Post then Unpost for one ERA and one EOB. Support's EOB link is the one
 * write still untested; it follows the spec. Every link is READ BACK: a 200 that changed nothing fails here rather than
 * reporting success. Unpost (`null`) removes only links posted by hand; the server keeps links it made by automatic
 * matching (backend, 2026-10-12), and nothing in a remit says which kind it has, so the read-back is where that shows.
 * `writesEnabled` is the console's switch, and `writesOff` says why it is off — demo mode keeps it off (FE-178).
 */
/** Why the write buttons are off by default: a console enables writes only once each is proven (NS-17). */
const WRITES_NOT_VERIFIED = 'Not enabled on this console yet: posting, unposting and ignoring are still being verified.';

export class ReconcileStore {
    @observable public accounts: IBankAccount[] = [];
    @observable public accountsError: string | undefined;
    @observable public itemId: string | undefined;

    @observable public tab: TransactionTab = 'UNPOSTED';
    @observable public filters: ITransactionFilters = {};
    @observable public sort: TransactionSort = 'DATE';
    @observable public order: 'ASCENDING' | 'DESCENDING' = 'DESCENDING';
    @observable public pageIndex = 0;
    @observable public pageSize = 20;
    @observable public transactions: IBankTransaction[] = [];
    @observable public total = 0;
    @observable public counts: Partial<Record<TransactionTab, number>> = {};
    @observable public loading = false;
    @observable public error: string | undefined;
    /** The remits each shown transaction is posted to, keyed by transaction id: "Posted to ERA 0084419". */
    @observable public postedTo = new Map<string, IRemit[]>();

    /** Unposted deposits in every account, and unposted remits of both kinds — the header. Unknown until counted. */
    @observable public unpostedDeposits: number | undefined;
    /** The header's counts could not be read, as opposed to still being read. */
    @observable public headerError = false;
    @observable public unpostedDepositsByItem = new Map<string, number>();
    @observable public unpostedRemits: number | undefined;

    @observable public selected: IBankTransaction | undefined;
    @observable public kind: RemitKind = 'ERA';
    @observable public matchOn: IMatchOn = { ...DEFAULT_MATCH_ON };
    /** Both kinds' counts for the current match — the ERA / EOB switch's labels. */
    @observable public kindCounts: Partial<Record<RemitKind, number>> = {};
    @observable public showPosted = false;
    @observable public candidateFilters: ICandidateFilters = {};
    @observable public candidates: IRemit[] = [];
    @observable public candidatesTotal = 0;
    /** Posted remits that would also match — the count on "Show posted". */
    @observable public postedMatches: number | undefined;
    @observable public candidatesLoading = false;
    @observable public candidatesError: string | undefined;
    /** Deposits behind the posted candidates, keyed by transaction id: "Posted · 250304128844710". */
    @observable public depositRefs = new Map<string, string>();

    @observable public flipped = false;
    /** Flipped: the remit being posted, picked first. */
    @observable public selectedRemit: IRemit | undefined;
    @observable public busy: string | undefined;
    @observable public writeError: string | undefined;
    @observable public lastUndo: IReconcileUndo | undefined;

    public readonly remits: IRemitsApi;
    public readonly banking: IBankingApi;
    public readonly writesEnabled: boolean;
    /** Why Post, Unpost and Ignore are off, as their tooltips say it. */
    public readonly writesOff: string;
    private request = 0;
    private countRequest = 0;
    private candidateRequest = 0;

    constructor({
        remits,
        banking,
        writesEnabled = false,
        writesOff = WRITES_NOT_VERIFIED,
    }: {
        remits: IRemitsApi;
        banking: IBankingApi;
        writesEnabled?: boolean;
        writesOff?: string;
    }) {
        this.remits = remits;
        this.banking = banking;
        this.writesEnabled = writesEnabled;
        this.writesOff = writesOff;
        makeObservable(this);
    }

    /** A fresh visit, as the Remits tab does: no filters, nothing selected, nothing cached. */
    @action
    public reset = (): void => {
        this.request++;
        this.countRequest++;
        this.candidateRequest++;
        this.loading = false;
        this.candidatesLoading = false;
        this.tab = 'UNPOSTED';
        this.filters = {};
        this.sort = 'DATE';
        this.order = 'DESCENDING';
        this.pageIndex = 0;
        this.transactions = [];
        this.total = 0;
        this.counts = {};
        this.error = undefined;
        this.postedTo.clear();
        this.selected = undefined;
        this.candidates = [];
        this.candidatesTotal = 0;
        this.postedMatches = undefined;
        this.candidateFilters = {};
        this.depositRefs.clear();
        this.selectedRemit = undefined;
        // Deposit first again: a visit that starts flipped would show an empty remit picker, since `start()` loads only
        // the bank side.
        this.flipped = false;
        this.writeError = undefined;
        this.lastUndo = undefined;
    };

    /** Accounts, then the first one's deposits and the header counts. */
    public start = async (): Promise<void> => {
        try {
            const accounts = await this.banking.listAccounts();
            runInAction(() => {
                this.accounts = accounts;
                this.accountsError = undefined;
                this.itemId = this.itemId && accounts.some(a => a.itemId === this.itemId) ? this.itemId : accounts[0]?.itemId;
            });
        } catch (err) {
            runInAction(() => (this.accountsError = errorText(err, 'The bank accounts could not be loaded.')));
            return;
        }
        void this.countHeader();
        await this.load();
    };

    @action
    public setAccount = (itemId: string): void => {
        if (itemId === this.itemId) {
            return;
        }
        this.itemId = itemId;
        this.pageIndex = 0;
        // Never the old account's rows under the new account's name while the new ones load.
        this.transactions = [];
        this.postedTo.clear();
        this.select(undefined);
        void this.load();
    };

    @action
    public setTab = (tab: TransactionTab): void => {
        if (tab === this.tab) {
            return;
        }
        this.tab = tab;
        this.pageIndex = 0;
        // The rows carry the tab's actions: an Unposted row must never show Restore while Ignored loads.
        this.transactions = [];
        this.select(undefined);
        void this.load({ recount: false });
    };

    @action
    public setFilters = (patch: Partial<ITransactionFilters>): void => {
        const next = { ...this.filters, ...patch };
        if ((Object.keys(next) as Array<keyof ITransactionFilters>).every(key => (next[key] || '') === (this.filters[key] || ''))) {
            return;
        }
        this.filters = next;
        this.pageIndex = 0;
        void this.load();
    };

    @action
    public setSort = (sort: TransactionSort): void => {
        // The same column again turns the order around, as the design's "Date ↓" does.
        this.order = sort === this.sort && this.order === 'DESCENDING' ? 'ASCENDING' : 'DESCENDING';
        this.sort = sort;
        this.pageIndex = 0;
        void this.load({ recount: false });
    };

    @action
    public setPage = (pageIndex: number, pageSize: number): void => {
        if (this.loading || pageIndex < 0 || (pageIndex === this.pageIndex && pageSize === this.pageSize && this.transactions.length)) {
            return;
        }
        this.pageIndex = pageIndex;
        this.pageSize = pageSize;
        void this.load({ recount: false });
    };

    /** The account's deposits on the current tab, and — unless asked not to — every tab's count with the same filters. */
    public load = async ({ recount = true }: { recount?: boolean } = {}): Promise<void> => {
        const itemId = this.itemId;
        if (!itemId) {
            return;
        }
        const request = ++this.request;
        if (this.remitPaysNothing) {
            // No deposit can carry it, so none is listed — not every deposit, which is what no amount filter would ask for.
            this.countRequest++;
            runInAction(() => {
                this.transactions = [];
                this.total = 0;
                this.counts = {};
                this.error = undefined;
                this.loading = false;
            });
            return;
        }
        const { tab, sort, order, pageSize, pageIndex } = this;
        const filters = { ...this.filters, ...this.depositMatch };
        runInAction(() => {
            this.loading = true;
            this.error = undefined;
        });
        if (recount) {
            const countRequest = ++this.countRequest;
            void Promise.all(
                TABS.map(each =>
                    this.banking
                        .listTransactions(itemId, { ...filters, tab: each, sort, order, limit: 1, offset: 0 })
                        .then(page => [each, page.total] as const, () => [each, undefined] as const),
                ),
            ).then(results => {
                // Its own guard: paging or sorting meanwhile must not throw away the counts for these filters.
                if (countRequest === this.countRequest) {
                    runInAction(() => (this.counts = Object.fromEntries(results.filter(([, n]) => n !== undefined))));
                }
            });
        }
        try {
            const page = await this.banking.listTransactions(itemId, {
                ...filters,
                tab,
                sort,
                order,
                limit: pageSize,
                offset: pageIndex * pageSize,
            });
            if (request !== this.request) {
                return;
            }
            // A page past the end — the last row of the last page was posted or ignored — goes back to the last page.
            if (!page.rows.length && page.total > 0 && pageIndex > 0) {
                runInAction(() => {
                    this.loading = false;
                    this.pageIndex = Math.max(0, Math.ceil(page.total / pageSize) - 1);
                });
                return this.load({ recount: false });
            }
            runInAction(() => {
                this.transactions = page.rows;
                this.total = page.total;
                this.counts = { ...this.counts, [tab]: page.total };
                this.loading = false;
            });
            if (tab === 'POSTED') {
                void this.loadPostedTo(page.rows);
            }
        } catch (err) {
            if (request !== this.request) {
                return;
            }
            runInAction(() => {
                this.transactions = [];
                this.total = 0;
                this.error = errorText(err, 'The bank transactions could not be loaded.');
                this.loading = false;
            });
        }
    };

    /**
     * Flipped, with a remit picked: the deposits that could carry it, by "Match on" the other way round — its amount, a
     * deposit within N days after it was paid, and a description that names its payment number. What the user typed into
     * the deposit filters wins, key by key, since two values of one filter would OR. The payment number goes to the server
     * as part of the description and is confirmed exactly per row (`visibleTransactions`), as is the payer.
     */
    /**
     * Flipped: the picked remit paid nothing. A $0 remit (every line denied or adjusted away, BPR02 0) moved no money, so no
     * deposit carries it: none is listed and Post Payment stays off.
     */
    @computed
    public get remitPaysNothing(): boolean {
        const remit = this.flipped ? this.selectedRemit : undefined;
        return !!remit && !(Number(remit.paidAmount) > 0);
    }

    private get depositMatch(): Partial<ITransactionQuery> {
        const remit = this.flipped ? this.selectedRemit : undefined;
        if (!remit) {
            return {};
        }
        const { amount, tolerance, paymentNumber, withinDays, days } = this.matchOn;
        const paid = Number(remit.paidAmount);
        const match: Partial<ITransactionQuery> = {};
        if (amount && Number.isFinite(paid) && !this.filters.amount) {
            if (tolerance > 0) {
                match.amountBetween = [(paid - tolerance).toFixed(2), (paid + tolerance).toFixed(2)];
            } else {
                match.amount = paid.toFixed(2);
            }
        }
        if (withinDays && remit.paidDate && !this.filters.date) {
            match.dateBetween = [remit.paidDate.slice(0, 10), shiftDay(remit.paidDate, days)];
        }
        if (paymentNumber && remit.paymentNumber && !this.filters.name) {
            match.name = remit.paymentNumber;
        }
        return match;
    }

    /**
     * The deposits on screen: flipped, only those whose payment number and payer the checked matches confirm. A typed Name
     * wins over the payment-number match here as it does in the query (`depositMatch`), or its results would vanish.
     */
    @computed
    public get visibleTransactions(): IBankTransaction[] {
        const remit = this.flipped ? this.selectedRemit : undefined;
        const { payer } = this.matchOn;
        const paymentNumber = this.matchOn.paymentNumber && !this.filters.name;
        if (!remit || (!payer && !(paymentNumber && remit.paymentNumber))) {
            return this.transactions;
        }
        return this.transactions.filter(
            deposit =>
                (!paymentNumber || !remit.paymentNumber || deposit.paymentNumber === remit.paymentNumber) &&
                (!payer || samePayer(deposit, remit)),
        );
    }

    /** The picked deposit "Same payer" narrows the remits to — none when a typed Payer name says which payer instead. */
    private get payerMatchDeposit(): IBankTransaction | undefined {
        return !this.flipped && this.matchOn.payer && !this.candidateFilters.payerName ? this.selected : undefined;
    }

    /** The remits on screen: for a picked deposit with "Same payer" checked, only those the payer confirms. */
    @computed
    public get visibleCandidates(): IRemit[] {
        const deposit = this.payerMatchDeposit;
        return deposit ? this.candidates.filter(remit => samePayer(deposit, remit)) : this.candidates;
    }

    /**
     * The ERA / EOB counts for the kind switch. The server counts without "Same payer", which is checked here on the rows
     * it returned, so while that applies the counts would overstate the list: none are shown rather than wrong ones.
     */
    @computed
    public get shownKindCounts(): Partial<Record<RemitKind, number>> {
        return this.payerMatchDeposit ? {} : this.kindCounts;
    }

    /** "Show posted (n)": counted without "Same payer" too, so — like the kind counts — not shown while it narrows the list. */
    @computed
    public get shownPostedMatches(): number | undefined {
        return this.payerMatchDeposit ? undefined : this.postedMatches;
    }

    /**
     * The "Match on" checks the list was actually narrowed by, either way round: a check counts only when the picked side
     * has the fact to match and no typed filter replaced it, key by key — as `candidateQuery`, `depositMatch` and the two
     * row filters apply them. What the page says it matched on.
     */
    @computed
    public get appliedMatch(): IMatchOn {
        const match = this.matchOn;
        const remit = this.flipped ? this.selectedRemit : undefined;
        if (remit) {
            return {
                ...match,
                amount: match.amount && Number.isFinite(Number(remit.paidAmount)) && !this.filters.amount,
                paymentNumber: match.paymentNumber && !!remit.paymentNumber && !this.filters.name,
                withinDays: match.withinDays && !!remit.paidDate && !this.filters.date,
            };
        }
        const deposit = this.flipped ? undefined : this.selected;
        if (!deposit) {
            return { ...match, amount: false, paymentNumber: false, withinDays: false, payer: false };
        }
        const typed = this.candidateFilters;
        return {
            ...match,
            amount: match.amount && !typed.amount,
            paymentNumber: match.paymentNumber && !!deposit.paymentNumber && !typed.paymentNumber,
            withinDays: match.withinDays && !!deposit.date && !typed.paidDate,
            payer: !!this.payerMatchDeposit,
        };
    }

    /**
     * Flipped: "Match on" emptied this page of deposits, and the account has others it has not looked at. The empty list
     * then says "on this page", never "in this account".
     */
    @computed
    public get depositsMatchedOnPageOnly(): boolean {
        return !this.visibleTransactions.length && this.transactions.length > 0 && this.total > this.transactions.length;
    }

    /** The remits past the first page that was fetched — and so never checked against "Same payer". */
    @computed
    public get uncheckedCandidates(): number {
        return this.candidates.length ? Math.max(0, this.candidatesTotal - this.candidates.length) : 0;
    }

    /** Which remit each posted deposit on screen went to: one query per kind for the whole page (repeated `EQ:` is an OR). */
    private loadPostedTo = async (rows: IBankTransaction[]): Promise<void> => {
        const ids = rows.map(row => row.id).filter(Boolean);
        if (!ids.length) {
            return;
        }
        const request = this.request;
        const byKind = await Promise.all(
            (['ERA', 'EOB'] as RemitKind[]).map(async kind => {
                const found: IRemit[] = [];
                try {
                    for (let offset = 0; ; offset += 100) {
                        const page = await this.remits.listRemits(kind, { limit: 100, offset, transactionIds: ids });
                        found.push(...page.rows);
                        if (page.rows.length < 100 || found.length >= page.total) {
                            break;
                        }
                    }
                } catch {
                    // Decoration: a row without its tag still says Posted.
                }
                return found;
            }),
        );
        if (request !== this.request) {
            return;
        }
        runInAction(() => {
            // Replaced, not merged: a remit unposted since must not linger on the deposit.
            ids.forEach(id => this.postedTo.delete(id));
            byKind.flat().forEach(remit => {
                const id = remit.plaidItemTransactionId;
                if (id && ids.includes(id)) {
                    const list = this.postedTo.get(id) || [];
                    if (!list.some(each => each.kind === remit.kind && each.id === remit.id)) {
                        this.postedTo.set(id, [...list, remit]);
                    }
                }
            });
        });
    };

    /** The header: unposted deposits across every account, and unposted remits of both kinds. */
    public countHeader = async (): Promise<void> => {
        const byItem = await Promise.all(
            this.accounts.map(account =>
                this.banking
                    .listTransactions(account.itemId, { tab: 'UNPOSTED', sort: 'DATE', order: 'DESCENDING', limit: 1, offset: 0 })
                    .then(page => [account.itemId, page.total] as const, () => [account.itemId, undefined] as const),
            ),
        );
        const remitCounts = await Promise.all(
            (['ERA', 'EOB'] as RemitKind[]).map(kind =>
                this.remits.listRemits(kind, { limit: 1, offset: 0, posted: false }).then(
                    page => page.total,
                    () => undefined,
                ),
            ),
        );
        runInAction(() => {
            this.headerError = byItem.some(([, n]) => n === undefined) || remitCounts.some(n => n === undefined);
            this.unpostedDepositsByItem = new Map(byItem.filter((e): e is readonly [string, number] => e[1] !== undefined));
            this.unpostedDeposits = byItem.every(([, n]) => n !== undefined)
                ? byItem.reduce((sum, [, n]) => sum + (n || 0), 0)
                : undefined;
            this.unpostedRemits = remitCounts.every(n => n !== undefined)
                ? remitCounts.reduce((sum: number, n) => sum + (n || 0), 0)
                : undefined;
        });
    };

    @action
    public select = (transaction: IBankTransaction | undefined): void => {
        this.selected = transaction;
        // Flipped, the remit list does not hang on a deposit, so changing the bank account or tab must not empty it.
        if (this.flipped) {
            return;
        }
        this.clearCandidates();
        if (transaction) {
            void this.loadCandidates();
        }
    };

    @action
    private clearCandidates = (): void => {
        // A reply for the previous deposit must not land after this one is chosen.
        this.candidateRequest++;
        this.candidates = [];
        this.candidatesTotal = 0;
        this.postedMatches = undefined;
        this.kindCounts = {};
        this.candidatesError = undefined;
    };

    @action
    public setKind = (kind: RemitKind): void => {
        if (kind !== this.kind) {
            this.kind = kind;
            // The other kind's rows go now, not when this kind's arrive: picked or posted meanwhile, one would act on a remit
            // the list no longer shows.
            this.candidates = [];
            this.candidatesTotal = 0;
            this.postedMatches = undefined;
            if (this.selectedRemit) {
                // Flipped: an ERA picked stays picked under the EOB list otherwise.
                this.selectRemit(undefined);
            }
            void this.loadCandidates();
        }
    };

    @action
    public setMatchOn = (patch: Partial<IMatchOn>): void => {
        const next = { ...this.matchOn, ...patch };
        const changed = (Object.keys(next) as Array<keyof IMatchOn>).filter(key => next[key] !== this.matchOn[key]);
        this.matchOn = next;
        // "Same payer" is checked on the rows already here; everything else asks the server again.
        if (!changed.some(key => key !== 'payer')) {
            return;
        }
        if (this.flipped) {
            // Flipped, the match narrows the deposits, not the remits.
            this.pageIndex = 0;
            void this.load();
        } else {
            void this.loadCandidates();
        }
    };

    @action
    public setShowPosted = (showPosted: boolean): void => {
        this.showPosted = showPosted;
        void this.loadCandidates();
    };

    @action
    public setCandidateFilters = (patch: Partial<ICandidateFilters>): void => {
        const next = { ...this.candidateFilters, ...patch };
        if ((Object.keys(next) as Array<keyof ICandidateFilters>).every(key => (next[key] || '') === (this.candidateFilters[key] || ''))) {
            return;
        }
        this.candidateFilters = next;
        void this.loadCandidates();
    };

    /**
     * The remit query: for the selected deposit, what "Match on" checks — its amount (within the tolerance), its payment
     * number, paid in the N days up to it — with a typed filter winning over its match, key by key. Flipped, the remits
     * come first, so only the typed filters apply.
     */
    public candidateQuery = (posted: boolean | undefined): IRemitQuery | undefined => {
        const deposit = this.selected?.deposit;
        if (deposit === undefined && !this.flipped) {
            return undefined;
        }
        const { payerName, paymentNumber, amount, paidDate } = this.candidateFilters;
        const near = (n: number): string => n.toFixed(2);
        const base: IRemitQuery = { limit: 50, offset: 0, payerName, paymentNumber, paidFrom: paidDate, paidTo: paidDate, posted };
        const picked = this.selected;
        if (deposit === undefined || !picked) {
            return { ...base, amount };
        }
        const match = this.matchOn;
        const query: IRemitQuery = { ...base };
        if (amount) {
            query.amount = amount;
        } else if (match.amount && match.tolerance > 0) {
            query.amountBetween = [near(deposit - match.tolerance), near(deposit + match.tolerance)];
        } else if (match.amount) {
            query.amount = near(deposit);
        }
        if (!paymentNumber && match.paymentNumber && picked.paymentNumber) {
            query.paymentNumberExact = picked.paymentNumber;
        }
        if (!paidDate && match.withinDays && picked.date) {
            query.paidFrom = shiftDay(picked.date, -match.days);
            query.paidTo = picked.date.slice(0, 10);
        }
        return query;
    };

    public loadCandidates = async (): Promise<void> => {
        const query = this.candidateQuery(this.showPosted ? undefined : false);
        const postedQuery = this.candidateQuery(true);
        if (!query || !postedQuery) {
            return;
        }
        const request = ++this.candidateRequest;
        const kind = this.kind;
        const other: RemitKind = kind === 'ERA' ? 'EOB' : 'ERA';
        runInAction(() => {
            this.candidatesLoading = true;
            this.candidatesError = undefined;
        });
        try {
            const [page, posted, otherCount] = await Promise.all([
                this.remits.listRemits(kind, query),
                this.remits.listRemits(kind, { ...postedQuery, limit: 1 }).then(p => p.total, () => undefined),
                // The other kind under the same match, for the switch's label. Decoration: a failure leaves it blank.
                this.remits.listRemits(other, { ...query, limit: 1 }).then(p => p.total, () => undefined),
            ]);
            if (request !== this.candidateRequest) {
                return;
            }
            runInAction(() => {
                this.candidates = page.rows;
                this.candidatesTotal = page.total;
                this.postedMatches = posted;
                this.kindCounts = { [kind]: page.total, [other]: otherCount };
                this.candidatesLoading = false;
            });
            // Another biller may have posted the picked remit since it was picked: the pick takes the row as listed now.
            this.refreshPickedRemit(false);
            void this.loadDepositRefs(page.rows);
        } catch (err) {
            if (request !== this.candidateRequest) {
                return;
            }
            runInAction(() => {
                this.candidates = [];
                this.candidatesTotal = 0;
                this.candidatesError = errorText(err, 'The remits could not be loaded.');
                this.candidatesLoading = false;
            });
        }
    };

    /** The bank reference of each deposit a shown remit is posted to — in whichever account it is (crosswalk §5). */
    private loadDepositRefs = async (rows: IRemit[]): Promise<void> => {
        const ids = [...new Set(rows.map(row => row.plaidItemTransactionId).filter((id): id is string => !!id))].filter(
            id => !this.depositRefs.has(id),
        );
        if (!ids.length) {
            return;
        }
        await Promise.all(
            this.accounts.map(account =>
                this.banking
                    .listTransactions(account.itemId, { tab: 'POSTED', ids, sort: 'DATE', order: 'DESCENDING', limit: ids.length, offset: 0 })
                    .then(
                        page => runInAction(() => page.rows.forEach(row => this.depositRefs.set(row.id, row.externalId || row.id))),
                        () => undefined, // Decoration: the row still says it is posted.
                    ),
            ),
        );
    };

    /** What is already posted to the selected deposit, and what of it is left — from the Posted tab's lookup. */
    @computed
    public get selectedPosted(): { remits: IRemit[]; posted: number; remaining: number } | undefined {
        const deposit = this.selected;
        if (!deposit) {
            return undefined;
        }
        const remits = this.postedTo.get(deposit.id) || [];
        const posted = Math.round(remits.reduce((sum, remit) => sum + Number(remit.paidAmount || 0), 0) * 100) / 100;
        return { remits, posted, remaining: Math.round((deposit.deposit - posted) * 100) / 100 };
    }

    /** Flipped: picks the remit to post, and narrows the deposits to the ones that could carry it. */
    @action
    public selectRemit = (remit: IRemit | undefined): void => {
        this.selectedRemit = remit;
        this.pageIndex = 0;
        // Never the last remit's matches, each with a Post button, under this remit while its own load.
        this.transactions = [];
        void this.load();
    };

    /** Deposit first, or remit first. Each way starts with nothing picked: a pick made for the other way means nothing here. */
    @action
    public flip = (): void => {
        this.flipped = !this.flipped;
        this.selected = undefined;
        this.clearCandidates();
        this.selectedRemit = undefined;
        this.pageIndex = 0;
        this.transactions = [];
        void this.load();
        if (this.flipped) {
            void this.loadCandidates();
        }
    };

    /** One write, then the lists it changed, then an Undo for it. Busy until the lists are current again. */
    private write = async (label: string, run: () => Promise<void>, undo: IReconcileUndo | undefined): Promise<boolean> => {
        if (!this.writesEnabled || this.busy) {
            return false;
        }
        runInAction(() => {
            this.busy = label;
            this.writeError = undefined;
            this.lastUndo = undefined;
        });
        try {
            await run();
        } catch (err) {
            const reason = await describe(err, 'the server did not accept it');
            runInAction(() => {
                this.busy = undefined;
                this.writeError = `${label} failed: ${reason}`;
            });
            // Both lists: a refused post may mean the picked remit was posted elsewhere, and the reload unpicks it.
            void this.load();
            void this.loadCandidates();
            return false;
        }
        void this.countHeader();
        await Promise.all([this.load(), this.loadCandidates()]);
        runInAction(() => {
            this.busy = undefined;
            this.lastUndo = undo;
        });
        this.refreshPickedRemit(true);
        return true;
    };

    /**
     * Flipped: the picked remit as the list now has it. A write may have changed its link — Undo of an Unpost posts it again
     * — and a pick that still says unposted would offer Post Payment for a remit that already has a deposit. A remit gone
     * from the list after a write, or posted now, is unpicked. A reload that only pages or filters it out of view keeps it.
     */
    private refreshPickedRemit = (dropWhenGone: boolean): void => {
        const picked = this.selectedRemit;
        if (!picked) {
            return;
        }
        const now = this.candidates.find(remit => remit.kind === picked.kind && remit.id === picked.id);
        if (!now && !dropWhenGone) {
            return;
        }
        if (!now || now.plaidItemTransactionId) {
            this.selectRemit(undefined);
        } else {
            runInAction(() => (this.selectedRemit = now));
        }
    };

    @action
    public dismissWriteError = (): void => {
        this.writeError = undefined;
    };

    /** Posts the remit to the selected deposit. The deposit then leaves Unposted, so nothing stays selected. */
    public post = async (remit: IRemit): Promise<boolean> => {
        const deposit = this.selected;
        if (!deposit) {
            return false;
        }
        const previous = remit.plaidItemTransactionId || null;
        const ok = await this.write('Post payment', () => this.link(remit, deposit.id), {
            done: `Posted ${remit.kind} ${remit.paymentNumber || remit.displayId} (${money(remit.paidAmount)}) to the ${money(deposit.deposit)} deposit.`,
            label: 'Undo',
            run: () => this.link(remit, previous),
        });
        if (ok) {
            this.select(undefined);
        }
        return ok;
    };

    /** Flipped: posts the selected remit to this deposit. The remit then leaves Unposted, so nothing stays picked. */
    public postTo = async (deposit: IBankTransaction): Promise<boolean> => {
        const remit = this.selectedRemit;
        // A posted remit is unposted first: the remit stores one deposit.
        if (!remit || remit.plaidItemTransactionId || this.remitPaysNothing) {
            return false;
        }
        const ok = await this.write('Post payment', () => this.linkIfUnposted(remit, deposit.id), {
            done: `Posted ${remit.kind} ${remit.paymentNumber || remit.displayId} (${money(remit.paidAmount)}) to the ${money(deposit.deposit)} deposit.`,
            label: 'Undo',
            run: () => this.link(remit, null),
        });
        if (ok) {
            this.selectRemit(undefined);
        }
        return ok;
    };

    /**
     * Sets the remit's deposit and reads the remit back: a 200 is not proof (NS-17). Unposting an automatic match answers
     * 200 and leaves the link in place — this is where that shows.
     */
    /**
     * Flipped, the pick can be minutes old and another biller may have posted it meanwhile: posting it again would move it
     * to this deposit (the remit stores one). The server's copy is read first, and a remit posted there is not touched.
     */
    private linkIfUnposted = async (remit: IRemit, transactionId: string): Promise<void> => {
        const page = await this.remits.listRemits(remit.kind, { limit: 1, offset: 0, ids: [remit.id] });
        if (page.rows[0]?.plaidItemTransactionId) {
            throw new Error('this remit is already posted to a deposit, perhaps by someone else meanwhile. Unpost it first to move it');
        }
        await this.link(remit, transactionId);
    };

    private link = async (remit: IRemit, transactionId: string | null): Promise<void> => {
        await this.remits.linkTransaction(remit, transactionId);
        const page = await this.remits.listRemits(remit.kind, { limit: 1, offset: 0, ids: [remit.id] });
        if (!page.rows.length) {
            throw new Error('the server answered OK but the remit could not be read back to check it');
        }
        const now = page.rows[0].plaidItemTransactionId || null;
        if (now !== transactionId) {
            throw new Error(
                transactionId
                    ? 'the server answered OK but the remit is not posted to this deposit'
                    : 'the server kept the link. Unpost removes only links posted by hand; a link made by automatic matching stays',
            );
        }
    };

    /** Takes the remit off its deposit. The page asks first. */
    public unpost = (remit: IRemit): Promise<boolean> => {
        const previous = remit.plaidItemTransactionId;
        if (!previous) {
            return Promise.resolve(false);
        }
        return this.write('Unpost', () => this.link(remit, null), {
            done: `Unposted ${remit.kind} ${remit.paymentNumber || remit.displayId} (${money(remit.paidAmount)}).`,
            label: 'Undo',
            run: () => this.link(remit, previous),
        });
    };

    public ignore = async (transaction: IBankTransaction): Promise<boolean> => {
        const ok = await this.write('Ignore', () => this.banking.ignore(transaction.itemId, transaction.id), {
            done: `Ignored the ${money(transaction.deposit)} transaction of ${transaction.date || 'unknown date'}.`,
            label: 'Undo',
            run: () => this.banking.restore(transaction.itemId, transaction.id),
        });
        if (ok && this.selected?.id === transaction.id) {
            this.select(undefined);
        }
        return ok;
    };

    public restore = (transaction: IBankTransaction): Promise<boolean> =>
        this.write('Restore', () => this.banking.restore(transaction.itemId, transaction.id), {
            done: `Restored the ${money(transaction.deposit)} transaction of ${transaction.date || 'unknown date'}.`,
            label: 'Undo',
            run: () => this.banking.ignore(transaction.itemId, transaction.id),
        });

    public undo = async (): Promise<void> => {
        const undo = this.lastUndo;
        if (undo) {
            await this.write(undo.label, undo.run, undefined);
        }
    };
}
